import React, { useState, useEffect } from 'react'
import { 
  Box, 
  Typography, 
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Theme
} from '@mui/material'
import { DeepcellTextField } from 'components/shared'
import { ExperimentData, ExperimentHandlers } from './types'
import WellsTable from './WellsTable'

interface DefineExperimentProps {
  experimentData: ExperimentData
  handlers: ExperimentHandlers
  theme: Theme
}

const DefineExperiment: React.FC<DefineExperimentProps> = ({ experimentData, handlers, theme }) => {
  const [cellsToImageError, setCellsToImageError] = useState<string>('')
  const [localCellsToImage, setLocalCellsToImage] = useState<string>(experimentData.cellsToImage === 0 ? '' : experimentData.cellsToImage.toString())
  
  // Update local state when props change from outside (but not when user is typing)
  useEffect(() => {
    if (document.activeElement?.getAttribute('type') !== 'number') {
      setLocalCellsToImage(experimentData.cellsToImage === 0 ? '' : experimentData.cellsToImage.toString())
    }
  }, [experimentData.cellsToImage])
  
  useEffect(() => {
    // Validate based on the actual numeric value
    const numericValue = localCellsToImage === '' ? 0 : parseInt(localCellsToImage, 10) || 0
    if (experimentData.experimentGoal === 'imaging' && numericValue === 0) {
      setCellsToImageError('Number of cells to image must be greater than 0')
    } else {
      setCellsToImageError('')
    }
  }, [localCellsToImage, experimentData.experimentGoal])
  
  // Handle cells to image change with local state
  const handleCellsToImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setLocalCellsToImage(value)
    
    // Update the actual data with proper conversion
    const numValue = value === '' ? 0 : parseInt(value, 10)
    if (!Number.isNaN(numValue)) {
      // Create a synthetic event that matches the expected type
      const syntheticEvent = {
        ...event,
        target: {
          ...event.target,
          value: numValue.toString()
        }
      } as React.ChangeEvent<HTMLInputElement>
      handlers.handleCellsToImageChange(syntheticEvent)
    }
  }
  
  return (
    <Box>
      <Box
        sx={{
          backgroundColor: theme.palette.info.light,
          p: 2,
          borderRadius: 1,
          mb: 2,
          borderBottom: `1px solid ${'#d0d0d0'}`,
          height: '73px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 400, color: theme.palette.text.primary, fontSize: '18px', lineHeight: '20px' }}
        >
          Step 1: Define Your Experiment
        </Typography>
      </Box>

      <Box sx={{ px: 1 }}>
        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
          <FormLabel
            component="legend"
            sx={{
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '16px',
            }}
          >
            Experiment Goal
          </FormLabel>
          <RadioGroup
            value={experimentData.experimentGoal}
            onChange={handlers.handleExperimentGoalChange}
            row
            sx={{ gap: 2 }}
          >
            <FormControlLabel value="imaging" control={<Radio />} label="Imaging" />
            <FormControlLabel value="purity" control={<Radio />} label="High Purity Sort" />
            <FormControlLabel value="yield" control={<Radio />} label="High Yield Sort" />
          </RadioGroup>
        </FormControl>


        {experimentData.experimentGoal === 'imaging' && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 400,
                color: theme.palette.text.primary,
                fontSize: '14px',
                lineHeight: '16px',
              }}
            >
              Number of Cells to Image
            </Typography>
            <DeepcellTextField
              value={localCellsToImage}
              onChange={handleCellsToImageChange}
              type="number"
              error={!!cellsToImageError}
              helperText={cellsToImageError}
              inputProps={{ min: 0 }}
            />
          </Box>
        )}



        {(experimentData.experimentGoal === 'purity' ||
          experimentData.experimentGoal === 'yield') && (
          <>
            <WellsTable
              experimentData={experimentData}
              handlers={{
                handleWellOutputChange: handlers.handleWellOutputChange,
                handleWellFrequencyChange: handlers.handleWellFrequencyChange
              }}
              theme={theme}
            />
            
            {/* Warning message for wells */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '14px',
                  color: theme.palette.warning.main,
                  lineHeight: '20px',
                  fontWeight: 400,
                }}
              >
                Warning: For large target cell counts, the sort may stop early to maintain performance. Consider reducing the per-well target or splitting across multiple wells.
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

export default DefineExperiment
